/**
 * NavigationTabs Component
 *
 * Tabs navigation for switching between multiple open task details
 * - Horizontal scrollable container
 * - Pill-shaped tabs with close buttons
 * - Active state highlighting
 * - Mobile responsive (horizontal scroll)
 */

import { memo, useEffect, useMemo, useRef } from 'react';
import { useReducedMotion } from '@mantine/hooks';
import { TabItem } from './TabItem';
import type { NavigationTabsProps } from './NavigationTabs.types';
import styles from './NavigationTabs.module.css';

export const NavigationTabs = memo(function NavigationTabs({
  tabs,
  activeTabId,
  onTabClick,
  onTabClose,
}: NavigationTabsProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const reduceMotion = useReducedMotion();

  // Memoize tab items to prevent unnecessary re-renders
  const tabItems = useMemo(() => {
    return tabs.map((tab) => (
      <TabItem
        key={tab.id}
        tab={tab}
        isActive={tab.id === activeTabId}
        onClick={() => onTabClick(tab.id)}
        onClose={(event) => onTabClose(tab.id, event)}
      />
    ));
  }, [tabs, activeTabId, onTabClick, onTabClose]);

  // Keep the active tab visible when opening / switching tabs in the
  // horizontally-scrolling strip (e.g. after opening many related artifacts).
  // Scroll only the strip horizontally — `scrollIntoView` would also scroll the
  // vertically-scrollable detail panel that wraps these tabs.
  useEffect(() => {
    const container = containerRef.current;
    const activeTab = container?.querySelector<HTMLElement>(
      '[aria-selected="true"]'
    );
    if (!container || !activeTab) return;

    const containerRect = container.getBoundingClientRect();
    const tabRect = activeTab.getBoundingClientRect();
    const behavior: ScrollBehavior = reduceMotion ? 'auto' : 'smooth';

    if (tabRect.left < containerRect.left) {
      container.scrollBy({ left: tabRect.left - containerRect.left, behavior });
    } else if (tabRect.right > containerRect.right) {
      container.scrollBy({
        left: tabRect.right - containerRect.right,
        behavior,
      });
    }
  }, [activeTabId, reduceMotion]);

  // Hide tabs if no navigation needed (0 or 1 tab)
  if (tabs.length <= 1) {
    return null;
  }

  return (
    <div ref={containerRef} className={styles.tabsContainer} role="tablist">
      <div className={styles.tabsList}>{tabItems}</div>
    </div>
  );
});
